import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Crown, ListTree, PenLine, ShieldCheck, ArrowRight } from 'lucide-react';
import { RUN, OUTLINE, DRAFT, VERIFY } from '@/content/premiumDemoReal';

const TABS = [
  {
    key: 'outline',
    icon: ListTree,
    label: 'Pass 1 · Outline',
    sub: `multi-perspective · ${RUN.durations.outline}`,
    note: 'Three expert perspectives (methodologist · clinician · editor) are reconciled into one outline, and every paragraph is given the question it has to answer before a single word is written.',
  },
  {
    key: 'draft',
    icon: PenLine,
    label: 'Pass 2 · Draft',
    sub: `per-subsection · ${RUN.durations.draft}`,
    note: 'Each part is drafted separately against its own retrieval (double the evidence of a normal run), with the already-written parts fed back in so the argument stays consistent.',
  },
  {
    key: 'verify',
    icon: ShieldCheck,
    label: 'Pass 3 · Verify & fix',
    sub: `${RUN.paragraphsVerified} paragraphs · ${RUN.durations.verify}`,
    note: 'Every paragraph is re-checked claim by claim against the retrieved chunks. What the library does not support is not written as fact — the limitation is stated instead.',
  },
];

const OutlinePane = () => (
  <div className="space-y-4">
    {OUTLINE.map((o, i) => (
      <div key={o.heading} className="border-l-2 border-[hsl(212,90%,55%)]/50 pl-4">
        <p className="font-mono text-[11px] text-[hsl(212,90%,72%)] mb-1">
          {String(i + 1).padStart(2, '0')}
        </p>
        <p className="text-[13.5px] text-slate-100">{o.heading}</p>
        <p className="text-[12px] text-slate-400 mt-1 leading-relaxed">? {o.question}</p>
      </div>
    ))}
  </div>
);

const DraftPane = () => (
  <div className="space-y-4">
    <p className="text-[13px] leading-[1.8] text-slate-100">
      {DRAFT.text}{' '}
      <span className="font-mono text-[11px] text-[hsl(212,90%,72%)]">{DRAFT.ref}</span>
    </p>
    <p className="text-[13px] leading-[1.8] text-slate-100">
      {DRAFT.text2}{' '}
      <span className="font-mono text-[11px] text-[hsl(212,90%,72%)]">{DRAFT.ref2}</span>
    </p>
    <div className="pt-3 border-t border-white/10 space-y-1.5">
      {DRAFT.refs.map((r) => (
        <p key={r} className="font-mono text-[11px] text-slate-400 leading-relaxed">{r}</p>
      ))}
    </div>
  </div>
);

const VerifyPane = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
      {[
        [`${RUN.verified}/${RUN.citations}`, 'citations resolved to a paper in the library'],
        [`${RUN.trust}%`, 'citation trust score for this section'],
        [`${RUN.paragraphsVerified}`, 'paragraphs audited claim by claim'],
      ].map(([v, l]) => (
        <div key={l} className="bg-[#0b1226] p-3">
          <p className="font-mono text-[20px] text-[hsl(212,90%,72%)]">{v}</p>
          <p className="text-[11px] text-slate-400 leading-snug mt-1">{l}</p>
        </div>
      ))}
    </div>
    <div className="rounded border border-emerald-400/40 bg-emerald-500/10 p-4">
      <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 mb-2">
        What it wrote instead of overclaiming
      </p>
      <p className="text-[13px] leading-[1.75] text-slate-100">{VERIFY.gapSentence}</p>
    </div>
    <div className="rounded border border-amber-400/40 bg-amber-500/10 p-3">
      <p className="text-[10px] font-mono uppercase tracking-widest text-amber-300 mb-1.5">
        Flagged for the author
      </p>
      <p className="text-[12.5px] text-slate-200">
        One citation token (<span className="font-mono text-[11px]">{VERIFY.unverifiedToken}</span>)
        could not be matched to an indexed paper and is marked
        <span className="font-mono text-[11px] text-amber-300"> unverified</span> in the editor
        rather than silently kept.
      </p>
    </div>
  </div>
);

const PANES = { outline: <OutlinePane />, draft: <DraftPane />, verify: <VerifyPane /> };

export const PremiumDemoSection = () => {
  const [active, setActive] = useState('draft');
  const tab = TABS.find((t) => t.key === active) || TABS[0];

  return (
    <section className="py-14 lg:py-20 bg-slate-900 text-white" data-testid="landing-premium-demo">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <p className="text-[11px] uppercase tracking-[0.2em] text-amber-400/90 font-mono mb-3">
          InkPhD Deep Writer
        </p>
        <div className="grid lg:grid-cols-[1fr_auto] gap-6 lg:items-end mb-10">
          <div>
            <h2 className="font-serif text-[26px] md:text-[34px] leading-tight mb-3">
              What five generations actually buy you.
            </h2>
            <p className="text-[14px] text-slate-300 max-w-2xl">
              Everything below is the unedited output of one real run on our demo project —
              a {RUN.section} written from {RUN.sources} indexed papers: {RUN.words.toLocaleString()} words,
              {' '}{RUN.subsections} parts, {RUN.paragraphsVerified} paragraphs audited,
              {' '}{RUN.trust}% citation trust. Three passes instead of one prompt is why a Deep Writer
              run counts as five generations.
            </p>
          </div>
          <div className="flex items-center gap-2 text-[12px] font-mono text-amber-300/90 shrink-0">
            <Crown className="w-4 h-4" /> Researcher: 5 / month · Doctoral &amp; Lab: unlimited
          </div>
        </div>

        <div className="grid lg:grid-cols-[320px_1fr] gap-px bg-white/10 border border-white/10">
          <div className="bg-slate-900">
            {TABS.map((t) => {
              const Icon = t.icon;
              const on = t.key === active;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActive(t.key)}
                  data-testid={`premium-demo-pass-${t.key}`}
                  className={`w-full text-left p-5 border-b border-white/10 last:border-b-0 transition-colors ${
                    on ? 'bg-white/[0.07]' : 'hover:bg-white/[0.04]'
                  }`}
                >
                  <span className="flex items-center gap-2 mb-1.5">
                    <Icon className={`w-4 h-4 ${on ? 'text-amber-400' : 'text-slate-400'}`} />
                    <span className={`text-[13px] font-semibold ${on ? 'text-white' : 'text-slate-300'}`}>
                      {t.label}
                    </span>
                  </span>
                  <span className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                    {t.sub}
                  </span>
                  <span className={`block text-[12.5px] leading-relaxed ${on ? 'text-slate-300' : 'text-slate-500'}`}>
                    {t.note}
                  </span>
                </button>
              );
            })}
          </div>

          <motion.div
            key={active}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="bg-[#0b1226] p-6 lg:p-8 min-h-[340px]"
            data-testid="premium-demo-output"
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-4">
              Real output · {tab.label} · {RUN.project}
            </p>
            {PANES[active]}
          </motion.div>
        </div>

        <p className="mt-4 text-[11.5px] text-slate-500 max-w-3xl" data-testid="premium-demo-disclaimer">
          Verbatim excerpts from a real run, shortened only for length. Your output depends entirely
          on the papers in your own library — the engine cannot cite anything you have not indexed,
          and any claim it cannot ground is flagged instead of written. Method note: the outline
          stage follows the STORM method (Stanford) and the verification stage the OpenScholar
          method (AI2); both are implemented inside InkPhD, which is not affiliated with either
          project.
        </p>

        <a href="#pricing"
          className="inline-flex items-center gap-2 mt-7 px-5 py-2.5 bg-amber-600 hover:bg-amber-500 transition-colors text-[13px] font-medium"
          data-testid="premium-demo-cta">
          See which plan includes it <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </section>
  );
};

export default PremiumDemoSection;
